/**
 * O tamanho da janela, fora do estado da mesa.
 *
 * Arrastar a borda dispara dezenas de eventos por segundo. Quando a largura
 * morava em `useEstado()`, cada quadro trocava o objeto inteiro e as quase
 * sessenta telas que o leem refaziam a árvore enquanto o navegador já refazia o
 * layout — os dois trabalhos mais caros da tela no mesmo quadro. Aqui é uma
 * assinatura com seletor: `useJanela((j) => j.largura < 760)` só redesenha
 * quando o BOOLEANO muda, e não a cada pixel.
 *
 * UM quadro por arrasto (o `requestAnimationFrame` descarta o que veio antes), e
 * nada quando o número não mudou.
 */
import { useRef, useSyncExternalStore } from "react";

let medida = null;
const ouvintes = new Set();
let quadro = 0;

const ler = () => {
  if (!medida || medida.largura !== window.innerWidth || medida.altura !== window.innerHeight)
    medida = { largura: window.innerWidth, altura: window.innerHeight };
  return medida;
};

const aoRedimensionar = () => {
  cancelAnimationFrame(quadro);
  quadro = requestAnimationFrame(() => {
    const antes = medida;
    if (ler() === antes) return;
    for (const avisar of ouvintes) avisar();
  });
};

function assinar(avisar) {
  /* Sem ninguém ouvindo, a janela pode ter mudado sem aviso: relê ao voltar. */
  if (!ouvintes.size) {
    ler();
    window.addEventListener("resize", aoRedimensionar);
  }
  ouvintes.add(avisar);
  return () => {
    ouvintes.delete(avisar);
    if (ouvintes.size) return;
    cancelAnimationFrame(quadro);
    window.removeEventListener("resize", aoRedimensionar);
  };
}

/* O instantâneo é o do último quadro, e não `window.innerWidth` ao vivo: ler ao
   vivo no render faria duas telas do mesmo quadro discordarem da largura. */
const instantaneo = () => medida ?? ler();

const inteira = (j) => j;

const mesmoRaso = (a, b) =>
  a === b ||
  (a &&
    b &&
    typeof a === "object" &&
    typeof b === "object" &&
    Object.keys(b).every((k) => Object.is(a[k], b[k])) &&
    Object.keys(a).length === Object.keys(b).length);

/**
 * `{ largura, altura }`, ou o que o seletor tirar dela.
 *
 * O seletor pode devolver um objeto de valores simples (a casca tira os
 * booleanos das colunas): igual campo a campo ao anterior, volta o MESMO objeto,
 * que é o que impede o `useSyncExternalStore` de redesenhar — e de entrar em
 * laço, já que um objeto novo a cada leitura seria "mudou" para sempre.
 */
export function useJanela(seletor = inteira) {
  const ultimo = useRef(undefined);
  return useSyncExternalStore(assinar, () => {
    const novo = seletor(instantaneo());
    if (mesmoRaso(ultimo.current, novo)) return ultimo.current;
    ultimo.current = novo;
    return novo;
  });
}
